import { useState, useMemo, type ReactNode } from 'react'
import { cn } from '../utils'
import { Plus, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Org, OrgMember, OrgRole, SentInvitation, ReceivedInvitation } from './types'
import type { OrgListItem } from './OrgsList'
import { OrgsList } from './OrgsList'
import { CreateOrgForm } from './CreateOrgForm'
import { OrgDetail } from './OrgDetail'

export type OrgViewProps = {
  /** All orgs the user belongs to (for the list table) */
  orgs: OrgListItem[]
  /** The currently-selected org (for the detail panel below the list) */
  org: Org | null
  members: OrgMember[]
  sentInvitations: SentInvitation[]
  receivedInvitations: ReceivedInvitation[]
  currentUserRole: OrgRole | null
  currentUserEmail: string
  loading: boolean
  loadingSlot?: ReactNode
  onCreateOrg?: (name: string) => Promise<void>
  onInviteMember?: (email: string) => Promise<void>
  onCancelInvitation?: (invitationId: string) => Promise<void>
  onRemoveMember?: (memberId: string) => Promise<void>
  onAcceptInvitation?: (id: string) => Promise<void>
  onDeclineInvitation?: (id: string) => Promise<void>
  selectedOrgId: string | null
  onSelectOrg?: (orgId: string | null) => void
  onLeaveOrg?: (orgId: string) => Promise<void>
  onDeleteOrg?: (orgId: string) => Promise<void>
  membersLoading?: boolean
  className?: string
}

function DefaultLoading() {
  return (
    <div className="space-y-4">
      <div className="h-24 rounded-lg bg-muted/30 animate-pulse" />
      <div className="h-40 rounded-lg bg-muted/30 animate-pulse" />
    </div>
  )
}

export function OrgView({
  orgs,
  org,
  members,
  sentInvitations,
  receivedInvitations,
  currentUserRole,
  loading,
  loadingSlot,
  onCreateOrg,
  onInviteMember,
  onCancelInvitation,
  onRemoveMember,
  onAcceptInvitation,
  onDeclineInvitation,
  selectedOrgId,
  onSelectOrg,
  onLeaveOrg,
  onDeleteOrg,
  membersLoading,
  className,
}: OrgViewProps) {
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Merge member orgs + pending invitations into a single list
  const allRows = useMemo<OrgListItem[]>(() => {
    const invitationRows: OrgListItem[] = receivedInvitations.map((inv) => ({
      id: inv.orgId,
      name: inv.orgName,
      role: 'pending' as const,
      invitationId: inv.id,
    }))
    return [...orgs, ...invitationRows]
  }, [orgs, receivedInvitations])

  if (loading) {
    return <div className={className}>{loadingSlot ?? <DefaultLoading />}</div>
  }

  const hasRows = allRows.length > 0

  return (
    <motion.div
      className={cn('space-y-6', className)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {/* Orgs list (member orgs + pending invitations merged) */}
      {hasRows && onSelectOrg && onLeaveOrg ? (
        <>
          <div className="flex items-center justify-end">
            {onCreateOrg && (
              <motion.button
                onClick={() => setShowCreateForm((v) => !v)}
                className={cn(
                  'inline-flex items-center justify-center rounded-md h-8 w-8 cursor-pointer transition-colors',
                  showCreateForm
                    ? 'border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-secondary'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
                whileTap={{ scale: 0.93 }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {showCreateForm ? (
                    <motion.span
                      key="cancel"
                      className="inline-flex items-center justify-center"
                      initial={{ opacity: 0, rotate: -90 }}
                      animate={{ opacity: 1, rotate: 0 }}
                      exit={{ opacity: 0, rotate: 90 }}
                      transition={{ duration: 0.15 }}
                    >
                      <X className="h-4 w-4" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="create"
                      className="inline-flex items-center justify-center"
                      initial={{ opacity: 0, rotate: 90 }}
                      animate={{ opacity: 1, rotate: 0 }}
                      exit={{ opacity: 0, rotate: -90 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Plus className="h-4 w-4" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            )}
          </div>

          <AnimatePresence initial={false}>
            {showCreateForm && onCreateOrg && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
                style={{ overflow: 'hidden' }}
              >
                <CreateOrgForm
                  onSubmit={async (name) => {
                    await onCreateOrg(name)
                    setShowCreateForm(false)
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <OrgsList
            orgs={allRows}
            selectedOrgId={selectedOrgId}
            onSelect={(id) => onSelectOrg(selectedOrgId === id ? null : id)}
            onLeave={onLeaveOrg}
            onDelete={onDeleteOrg}
            onAcceptInvitation={onAcceptInvitation}
            onDeclineInvitation={onDeclineInvitation}
          />
        </>
      ) : (
        /* No orgs or invitations: show create form */
        onCreateOrg && <CreateOrgForm onSubmit={onCreateOrg} />
      )}

      {/* Selected org detail (header hidden — OrgsList replaces it) */}
      <AnimatePresence mode="wait">
        {org && (
          <motion.div
            key={org.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <OrgDetail
              org={org}
              members={members}
              sentInvitations={sentInvitations}
              currentUserRole={currentUserRole}
              onInviteMember={onInviteMember}
              onCancelInvitation={onCancelInvitation}
              onRemoveMember={onRemoveMember}
              membersLoading={membersLoading}
              hideHeader
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
